'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import StarRating from '@/components/ui/StarRating';
import { toast } from 'sonner';

const customers = [
  {
    id: 'cust-001',
    name: 'Rachel Kim',
    email: 'rachel.kim@email.com',
    phone: '+1 (212) 555-0142',
    joinDate: 'Jan 14, 2025',
    totalBookings: 18,
    totalSpent: 42800,
    status: 'active' as const,
    rating: 4.9,
    type: 'Premium',
    lastBooking: 'Apr 22, 2026',
    location: 'New York, NY',
  },
  {
    id: 'cust-002',
    name: 'David Okonkwo',
    email: 'd.okonkwo@corp.com',
    phone: '+1 (646) 555-0318',
    joinDate: 'Mar 5, 2025',
    totalBookings: 7,
    totalSpent: 8940,
    status: 'active' as const,
    rating: 4.7,
    type: 'Corporate',
    lastBooking: 'Apr 22, 2026',
    location: 'Brooklyn, NY',
  },
  {
    id: 'cust-003',
    name: 'Samantha Torres',
    email: 'sam.torres@gmail.com',
    phone: '+1 (917) 555-0089',
    joinDate: 'Jun 22, 2024',
    totalBookings: 24,
    totalSpent: 61200,
    status: 'active' as const,
    rating: 5.0,
    type: 'Premium',
    lastBooking: 'Apr 21, 2026',
    location: 'Manhattan, NY',
  },
  {
    id: 'cust-004',
    name: 'Marcus Webb',
    email: 'mwebb@webdev.io',
    phone: '+1 (718) 555-0274',
    joinDate: 'Sep 3, 2024',
    totalBookings: 11,
    totalSpent: 22400,
    status: 'active' as const,
    rating: 4.6,
    type: 'Standard',
    lastBooking: 'Apr 18, 2026',
    location: 'Queens, NY',
  },
  {
    id: 'cust-005',
    name: 'Yuki Tanaka',
    email: 'yuki.t@outlook.com',
    phone: '+1 (332) 555-0401',
    joinDate: 'Feb 17, 2026',
    totalBookings: 3,
    totalSpent: 640,
    status: 'pending' as const,
    rating: 4.2,
    type: 'Standard',
    lastBooking: 'Apr 20, 2026',
    location: 'Hoboken, NJ',
  },
  {
    id: 'cust-006',
    name: 'Ibrahim Al-Sayed',
    email: 'ibrahim@alsayed.ae',
    phone: '+971 50 555 0192',
    joinDate: 'Nov 8, 2024',
    totalBookings: 14,
    totalSpent: 38600,
    status: 'active' as const,
    rating: 4.8,
    type: 'Premium',
    lastBooking: 'Apr 20, 2026',
    location: 'Dubai (visiting)',
  },
  {
    id: 'cust-007',
    name: 'Preethi Nair',
    email: 'preethi.n@nair.in',
    phone: '+91 98765 43210',
    joinDate: 'Apr 1, 2026',
    totalBookings: 2,
    totalSpent: 190,
    status: 'active' as const,
    rating: 3.8,
    type: 'Standard',
    lastBooking: 'Apr 18, 2026',
    location: 'Mumbai (visiting)',
  },
  {
    id: 'cust-008',
    name: 'Lena Hoffmann',
    email: 'l.hoffmann@dmail.de',
    phone: '+49 171 555 0283',
    joinDate: 'Dec 12, 2024',
    totalBookings: 9,
    totalSpent: 19400,
    status: 'active' as const,
    rating: 4.5,
    type: 'Corporate',
    lastBooking: 'Apr 15, 2026',
    location: 'Berlin (visiting)',
  },
  {
    id: 'cust-009',
    name: 'Carlos Mendez',
    email: 'c.mendez@hotmail.com',
    phone: '+1 (305) 555-0167',
    joinDate: 'Jul 29, 2025',
    totalBookings: 5,
    totalSpent: 3200,
    status: 'active' as const,
    rating: 4.4,
    type: 'Standard',
    lastBooking: 'Apr 10, 2026',
    location: 'Miami, FL',
  },
  {
    id: 'cust-010',
    name: 'Ananya Krishnan',
    email: 'ananya.k@techco.in',
    phone: '+1 (408) 555-0339',
    joinDate: 'Oct 14, 2025',
    totalBookings: 6,
    totalSpent: 7800,
    status: 'cancelled' as const,
    rating: 3.2,
    type: 'Standard',
    lastBooking: 'Apr 2, 2026',
    location: 'San Jose, CA',
  },
];

const typeColors: Record<string, string> = {
  Premium: 'bg-accent/15 text-amber-700',
  Corporate: 'bg-blue-50 text-blue-700',
  Standard: 'bg-muted text-muted-foreground',
};

export default function CustomersList() {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortCol, setSortCol] = useState('totalSpent');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = customers
    .filter((c) => {
      const q = search.toLowerCase();
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
      const matchType = filterType === 'all' || c.type === filterType;
      return matchSearch && matchType;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortCol === 'totalSpent') return (a.totalSpent - b.totalSpent) * dir;
      if (sortCol === 'totalBookings') return (a.totalBookings - b.totalBookings) * dir;
      if (sortCol === 'name') return a.name.localeCompare(b.name) * dir;
      if (sortCol === 'rating') return (a.rating - b.rating) * dir;
      return 0;
    });

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: string }) => (
    <span className="ml-1 inline-flex flex-col gap-px">
      <svg width="8" height="5" viewBox="0 0 8 5" className={sortCol === col && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'}>
        <path d="M4 0L8 5H0L4 0Z" fill="currentColor"/>
      </svg>
      <svg width="8" height="5" viewBox="0 0 8 5" className={sortCol === col && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'}>
        <path d="M4 5L0 0H8L4 5Z" fill="currentColor"/>
      </svg>
    </span>
  );

  return (
    <div className="bg-white rounded-xl border border-border shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div>
          <h2 className="font-display font-semibold text-base text-foreground">Customer Directory</h2>
          <p className="text-xs text-muted-foreground">{filtered.length} registered customers</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-muted">
            <Icon name="MagnifyingGlassIcon" size={15} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Search customers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent text-sm outline-none w-36 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm text-foreground bg-white outline-none cursor-pointer"
          >
            <option value="all">All Types</option>
            <option value="Premium">Premium</option>
            <option value="Corporate">Corporate</option>
            <option value="Standard">Standard</option>
          </select>
          <button
            onClick={() => toast.success('Customer list exported to CSV.')}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="ArrowDownTrayIcon" size={15} />
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {[
                { key: 'name', label: 'Customer' },
                { key: 'type', label: 'Type' },
                { key: 'location', label: 'Location' },
                { key: 'joinDate', label: 'Member Since' },
                { key: 'totalBookings', label: 'Bookings' },
                { key: 'totalSpent', label: 'Total Spent' },
                { key: 'rating', label: 'Rating' },
                { key: 'lastBooking', label: 'Last Booking' },
                { key: 'status', label: 'Status' },
              ].map((col) => (
                <th
                  key={`cust-th-${col.key}`}
                  onClick={() => ['name', 'totalBookings', 'totalSpent', 'rating'].includes(col.key) ? toggleSort(col.key) : undefined}
                  className={`px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${
                    ['name', 'totalBookings', 'totalSpent', 'rating'].includes(col.key)
                      ? 'cursor-pointer hover:text-foreground select-none' :''
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {['name', 'totalBookings', 'totalSpent', 'rating'].includes(col.key) && <SortIcon col={col.key} />}
                  </span>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="UsersIcon" size={24} className="text-muted-foreground" />
                    </div>
                    <p className="font-display font-semibold text-foreground">No customers found</p>
                    <p className="text-sm text-muted-foreground">Adjust your search or filter to find customers</p>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((cust, idx) => (
                <tr
                  key={cust.id}
                  className={`border-b border-border/50 hover:bg-muted/30 transition-colors group ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">
                          {cust.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground whitespace-nowrap">{cust.name}</p>
                        <p className="text-xs text-muted-foreground">{cust.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeColors[cust.type]}`}>
                      {cust.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{cust.location}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{cust.joinDate}</td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{cust.totalBookings}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-bold text-foreground tabular-nums">${cust.totalSpent.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <StarRating value={cust.rating} size={12} />
                      <span className="text-xs text-muted-foreground tabular-nums">{cust.rating}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{cust.lastBooking}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={cust.status} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        title="View customer profile"
                        onClick={() => toast.success(`Viewing profile for ${cust.name}`)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Icon name="EyeIcon" size={15} />
                      </button>
                      <button
                        title="Send message to customer"
                        onClick={() => toast.success(`Messaging ${cust.name}`)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Icon name="ChatBubbleLeftIcon" size={15} />
                      </button>
                      <button
                        title="Suspend customer account"
                        onClick={() => toast.error(`Account for ${cust.name} suspended.`)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Icon name="NoSymbolIcon" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer stats */}
      <div className="px-6 py-4 border-t border-border bg-muted/20 flex flex-wrap items-center gap-6 text-sm">
        <span className="text-muted-foreground">
          Total customers: <span className="font-semibold text-foreground">{customers.length}</span>
        </span>
        <span className="text-muted-foreground">
          Total revenue: <span className="font-semibold text-foreground tabular-nums">
            ${customers.reduce((s, c) => s + c.totalSpent, 0).toLocaleString()}
          </span>
        </span>
        <span className="text-muted-foreground">
          Avg. spend: <span className="font-semibold text-foreground tabular-nums">
            ${Math.round(customers.reduce((s, c) => s + c.totalSpent, 0) / customers.length).toLocaleString()}
          </span>
        </span>
        <span className="text-muted-foreground">
          Premium members: <span className="font-semibold text-foreground">
            {customers.filter((c) => c.type === 'Premium').length}
          </span>
        </span>
      </div>
    </div>
  );
}